/**
 * PrefixInput — a text field with a fixed, non-editable leading prefix (e.g. "Dr. ").
 *
 * The user types only the variable part; the prefix is shown as a static chip and can't be
 * edited or deleted. The component reports the FULL value (`prefix + typed`) via `onChange`, so
 * the payload sent to the backend includes the prefix. An empty field reports `""` (not a bare
 * prefix), so optional fields stay empty.
 *
 * `value` may be the full string or just the variable part — a leading prefix (case-insensitive)
 * is stripped for display so it round-trips cleanly.
 */

interface PrefixInputProps {
  prefix: string;
  value: string;
  onChange: (full: string) => void;
  label?: string;
  required?: boolean;
  placeholder?: string;
  autoFocus?: boolean;
  className?: string;
  /** Filters the typed part (not the prefix), e.g. to drop disallowed characters. */
  sanitize?: (typed: string) => string;
}

function stripPrefix(value: string, prefix: string): string {
  const v = value ?? "";
  return v.toLowerCase().startsWith(prefix.toLowerCase()) ? v.slice(prefix.length) : v;
}

export function PrefixInput({
  prefix,
  value,
  onChange,
  label,
  required,
  placeholder,
  autoFocus,
  className = "",
  sanitize,
}: PrefixInputProps) {
  const rest = stripPrefix(value, prefix);

  const handle = (raw: string) => {
    const cleaned = sanitize ? sanitize(raw) : raw;
    const trimmed = cleaned.replace(/^\s+/, ""); // no leading spaces right after the prefix
    onChange(trimmed ? `${prefix}${trimmed}` : "");
  };

  const field = (
    <div
      className={`flex items-stretch overflow-hidden rounded-xl border border-line bg-surface shadow-xs transition hover:border-line-strong focus-within:border-brand focus-within:ring-2 focus-within:ring-brand/25 ${className}`}
    >
      <span className="grid select-none place-items-center border-r border-line bg-sunken px-3 text-sm font-medium text-ink-soft">
        {prefix.trim()}
      </span>
      <input
        type="text"
        value={rest}
        onChange={(e) => handle(e.target.value)}
        placeholder={placeholder}
        required={required}
        autoFocus={autoFocus}
        className="w-full border-0 bg-transparent px-3 py-2.5 text-sm text-ink outline-hidden placeholder:text-ink-faint focus:ring-0"
      />
    </div>
  );

  if (!label) return field;
  return (
    <label className="flex flex-col gap-1 text-sm">
      <span className="label mb-0">
        {label}
        {required && <span className="ml-0.5 text-danger" aria-hidden="true">*</span>}
      </span>
      {field}
    </label>
  );
}
